import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { I18nProvider } from '@/i18n/I18nProvider';
import { AuthProvider } from '@/contexts/AuthContext';
import { ActiveCompanyProvider } from '@/contexts/ActiveCompanyContext';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { RequireRole } from '@/components/auth/RequireRole';
import { Layout } from '@/components/layout/Layout';

const Login = lazy(async () => ({ default: (await import('@/pages/Login')).Login }));
const PasswordChange = lazy(async () => ({
  default: (await import('@/pages/PasswordChange')).PasswordChange,
}));
const Unavailable = lazy(async () => ({ default: (await import('@/pages/Unavailable')).Unavailable }));
const Home = lazy(async () => ({ default: (await import('@/pages/Home')).Home }));
const Setting = lazy(async () => ({ default: (await import('@/pages/Setting')).Setting }));
const NotFound = lazy(async () => ({ default: (await import('@/pages/NotFound')).NotFound }));

const CompanyList = lazy(async () => ({ default: (await import('@/pages/companies/CompanyList')).CompanyList }));
const CompanyForm = lazy(async () => ({ default: (await import('@/pages/companies/CompanyForm')).CompanyForm }));
const ContractList = lazy(async () => ({ default: (await import('@/pages/contracts/ContractList')).ContractList }));
const ContractForm = lazy(async () => ({ default: (await import('@/pages/contracts/ContractForm')).ContractForm }));
const UserSearch = lazy(async () => ({ default: (await import('@/pages/users/UserSearch')).UserSearch }));
const StudioList = lazy(async () => ({ default: (await import('@/pages/studios/StudioList')).StudioList }));
const StudioForm = lazy(async () => ({ default: (await import('@/pages/studios/StudioForm')).StudioForm }));
const StudentList = lazy(async () => ({ default: (await import('@/pages/students/StudentList')).StudentList }));
const StudentForm = lazy(async () => ({ default: (await import('@/pages/students/StudentForm')).StudentForm }));
const InstructorList = lazy(async () => ({
  default: (await import('@/pages/instructors/InstructorList')).InstructorList,
}));
const InstructorForm = lazy(async () => ({
  default: (await import('@/pages/instructors/InstructorForm')).InstructorForm,
}));
const PoseList = lazy(async () => ({ default: (await import('@/pages/poses/PoseList')).PoseList }));
const PoseDetail = lazy(async () => ({ default: (await import('@/pages/poses/PoseDetail')).PoseDetail }));
const PoseForm = lazy(async () => ({ default: (await import('@/pages/poses/PoseForm')).PoseForm }));
const PoseCategoryList = lazy(async () => ({
  default: (await import('@/pages/pose-categories/PoseCategoryList')).PoseCategoryList,
}));
const PoseCategoryForm = lazy(async () => ({
  default: (await import('@/pages/pose-categories/PoseCategoryForm')).PoseCategoryForm,
}));
const MatList = lazy(async () => ({ default: (await import('@/pages/mats/MatList')).MatList }));
const MatForm = lazy(async () => ({ default: (await import('@/pages/mats/MatForm')).MatForm }));
const AdminAccountList = lazy(async () => ({
  default: (await import('@/pages/admin-accounts/AdminAccountList')).AdminAccountList,
}));
const AdminAccountForm = lazy(async () => ({
  default: (await import('@/pages/admin-accounts/AdminAccountForm')).AdminAccountForm,
}));
const LessonList = lazy(async () => ({ default: (await import('@/pages/lessons/LessonList')).LessonList }));
const LessonForm = lazy(async () => ({ default: (await import('@/pages/lessons/LessonForm')).LessonForm }));
const SequenceList = lazy(async () => ({ default: (await import('@/pages/sequences/SequenceList')).SequenceList }));
const SequenceForm = lazy(async () => ({ default: (await import('@/pages/sequences/SequenceForm')).SequenceForm }));

export function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<div className="min-h-screen bg-canvas" />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/password-change" element={<PasswordChange />} />
              <Route path="/unavailable" element={<Unavailable />} />

              <Route
                element={
                  <ProtectedRoute>
                    <ActiveCompanyProvider>
                      <Layout />
                    </ActiveCompanyProvider>
                  </ProtectedRoute>
                }
              >
                <Route path="/" element={<Home />} />
                <Route path="/setting" element={<Setting />} />

                <Route element={<RequireRole allow={['admin']} />}>
                  <Route path="/companies" element={<CompanyList />} />
                  <Route path="/companies/new" element={<CompanyForm />} />
                  <Route path="/companies/:companyId/edit" element={<CompanyForm />} />
                  {/* #2133 PR-C(design-phase2.md §4): 契約情報(admin専用、全社横断の一覧) */}
                  <Route path="/contracts" element={<ContractList />} />
                  <Route path="/contracts/new" element={<ContractForm />} />
                  <Route path="/contracts/:contractId/edit" element={<ContractForm />} />
                  {/* #2133 設計 §3: 会社をまたいだユーザー検索(admin 専用)。 */}
                  <Route path="/users" element={<UserSearch />} />
                </Route>

                {/* #2133(社長指示2026-10-13): admin は会社一覧で選んだ会社に対して、
                    companyAssociate と同じ画面を使える(ActiveCompanyContext)。 */}
                <Route element={<RequireRole allow={['companyAssociate', 'admin']} />}>
                  <Route path="/studios" element={<StudioList />} />
                  <Route path="/studios/new" element={<StudioForm />} />
                  <Route path="/studios/:studioId/edit" element={<StudioForm />} />
                  {/* #2133 PR#3: 管理者アカウントの新規作成は companyAssociate のみ
                      (studioAssociate は作成できない。CONTROLLABLE_TARGET_ROLES 参照)。
                      admin はこの制約を受けない(admin 専用の既存動作)ため同様に追加する。 */}
                  <Route path="/admin-accounts/new" element={<AdminAccountForm />} />
                  {/* #2218: 編集も companyAssociate のみ(CONTROLLABLE_TARGET_ROLES
                      [studioAssociate] は ['student'] のみのため studioAssociate は
                      他の管理者アカウントを編集できない) */}
                  <Route path="/admin-accounts/:userId/edit" element={<AdminAccountForm />} />
                </Route>

                <Route element={<RequireRole allow={['companyAssociate', 'studioAssociate', 'admin']} />}>
                  <Route path="/students" element={<StudentList />} />
                  <Route path="/students/new" element={<StudentForm />} />
                  <Route path="/students/:userId/edit" element={<StudentForm />} />
                  <Route path="/instructors" element={<InstructorList />} />
                  <Route path="/instructors/new" element={<InstructorForm />} />
                  <Route path="/instructors/:userId/edit" element={<InstructorForm />} />
                  <Route path="/mats" element={<MatList />} />
                  <Route path="/mats/new" element={<MatForm />} />
                  <Route path="/mats/:matId/edit" element={<MatForm />} />
                  {/* #2133 PR#3: 一覧は company/studio の両ロールが読める
                      (studioAssociate は自スタジオのみの読み取り専用) */}
                  <Route path="/admin-accounts" element={<AdminAccountList />} />
                  <Route path="/lessons" element={<LessonList />} />
                  <Route path="/lessons/new" element={<LessonForm />} />
                  <Route path="/lessons/:lessonId/edit" element={<LessonForm />} />
                  {/* #2133 PR#6: シーケンス(ポーズを選んで並べて作成・編集・削除) */}
                  <Route path="/sequences" element={<SequenceList />} />
                  <Route path="/sequences/new" element={<SequenceForm />} />
                  <Route path="/sequences/:sequenceId/edit" element={<SequenceForm />} />
                </Route>

                {/* #2133(社長指示2026-10-13): admin も会社を選んだ上でポーズ一覧を
                    読む(studio を選ぶ UI は PoseList.tsx 側で出す)。 */}
                <Route element={<RequireRole allow={['companyAssociate', 'studioAssociate', 'admin']} />}>
                  <Route path="/poses" element={<PoseList />} />
                  {/* #2133 設計第3弾 §3 項目1/2: 新規作成・編集。/poses/:poseId
                      (翻訳編集)より先に具体的なパスを置く必要はない
                      (react-router は静的セグメント"new"/"edit"より前に
                      :poseId を定義しても、固定文字列のマッチが優先される)。 */}
                  <Route path="/poses/new" element={<PoseForm />} />
                  <Route path="/poses/:poseId/edit" element={<PoseForm />} />
                  <Route path="/poses/:poseId" element={<PoseDetail />} />
                  {/* #2133 設計第3弾 §3 項目3: ポーズカテゴリ(poses と同じロール)。 */}
                  <Route path="/pose-categories" element={<PoseCategoryList />} />
                  <Route path="/pose-categories/new" element={<PoseCategoryForm />} />
                  <Route path="/pose-categories/:categoryId/edit" element={<PoseCategoryForm />} />
                </Route>
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}
